import classNames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useMediaQuery } from 'react-responsive';

import Image from 'components/ImageV2';
import { IMAGE_WIDTHS } from 'constants/images';
import { largeUp, mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

import { VARIATIONS, VARIATION_DIMENSIONS, BREAKPOINTS, ALIGNMENT_OPTIONS } from './constants';
import Details from './Details';

const CollectionBanner = ({
  title,
  description,
  bgColor,
  bannerSrc,
  mobileBannerSrc,
  variation,
  eyebrow,
  fullBleedAlignmentMobile,
  fullBleedAlignmentDesktop,
}) => {
  const isMediumUp = useMediaQuery({ query: mediumUp });
  const breakpoint = isMediumUp ? BREAKPOINTS.MEDIUM_UP : BREAKPOINTS.SMALL;
  const imageSrc = !isMediumUp && mobileBannerSrc ? mobileBannerSrc : bannerSrc;
  const isFullBleed = variation === VARIATIONS.FULL_BLEED;
  const isSideAligned = isMediumUp
    ? fullBleedAlignmentDesktop !== ALIGNMENT_OPTIONS.CENTER
    : fullBleedAlignmentMobile !== ALIGNMENT_OPTIONS.CENTER;

  return (
    <div className="CollectionBanner">
      <div
        className={classNames('CollectionBanner__content', {
          'CollectionBanner__content--full-bleed': isFullBleed,
        })}
      >
        <div
          className={classNames('CollectionBanner__hero-container', {
            'CollectionBanner__hero-container--full-bleed': isFullBleed,
          })}
        >
          <Image
            src={imageSrc}
            alt=""
            width={IMAGE_WIDTHS.W_3600}
            ratio={get(VARIATION_DIMENSIONS[variation].IMAGE_RATIOS, breakpoint)}
          />
        </div>
        <div
          className={classNames('CollectionBanner__details', {
            'CollectionBanner__details--full-bleed-center': isFullBleed && !isSideAligned,
            'CollectionBanner__details--full-bleed-side': isFullBleed && isSideAligned,
          })}
        >
          <Details
            title={title}
            description={description}
            eyebrow={eyebrow}
            variation={variation}
            isSideAligned={isSideAligned}
          />
        </div>
      </div>
      <style jsx>
        {`
          .CollectionBanner {
            background-color: ${bgColor};
          }

          .CollectionBanner__content {
            display: flex;
            flex-direction: column;
            width: 100%;
          }

          .CollectionBanner__hero-container {
            width: 100%;
          }

          .CollectionBanner__details {
            width: 100%;
            text-align: center;
            flex: 1;
          }

          /** Mobile Full Bleed Banner **/
          .CollectionBanner__content--full-bleed {
            position: relative;
          }

          .CollectionBanner__details--full-bleed-center,
          .CollectionBanner__details--full-bleed-side {
            position: absolute;
          }

          .CollectionBanner__details--full-bleed-center {
            transform: translateY(-50%);
            top: 50%;
          }

          .CollectionBanner__details--full-bleed-side {
            transform: translateY(-100%);
            top: 100%;
          }

          @media ${largeUp} {
            .CollectionBanner__content {
              flex-direction: row;
            }

            .CollectionBanner__hero-container {
              width: 65%;
              max-width: ${IMAGE_WIDTHS.W_1600}px;
            }

            .CollectionBanner__details {
              width: 35%;
            }

            /** Full Bleed Banner **/
            .CollectionBanner__content--full-bleed {
              position: relative;
            }

            .CollectionBanner__hero-container--full-bleed {
              width: 100%;
              max-width: ${IMAGE_WIDTHS.W_1800}px;
            }

            .CollectionBanner__details--full-bleed-center,
            .CollectionBanner__details--full-bleed-side {
              width: 100%;
            }

            .CollectionBanner__details--full-bleed-side {
              transform: translateY(-50%);
              top: 50%;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .CollectionBanner__title.typography---primary-heading,
          .CollectionBanner__description.Paragraph {
            color: ${Theme.get('colors-white')};
          }

          .CollectionBanner__description.Paragraph {
            margin-bottom: 0;
          }
        `}
      </style>
    </div>
  );
};

CollectionBanner.propTypes = {
  bannerSrc: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  bgColor: PropTypes.string.isRequired,
  variation: PropTypes.string.isRequired,
  fullBleedAlignmentMobile: PropTypes.string.isRequired,
  fullBleedAlignmentDesktop: PropTypes.string.isRequired,
  mobileBannerSrc: PropTypes.string,
  eyebrow: PropTypes.string,
};

CollectionBanner.defaultProps = {
  mobileBannerSrc: null,
  eyebrow: '',
};

export default CollectionBanner;
